import React, { useCallback } from 'react';
import {
  Avatar,
  Box,
  Chip,
  IconButton,
  ListItemButton,
  Typography,
} from '@mui/material';
import { MoreHorizontal as MoreHorizontalIcon } from 'lucide-react';

export interface UserListItemProps {
  id: string;
  fullName: string;
  email: string;
  roles: string[];
  status?: string;
  loading: boolean;
  onOpenMenu: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  onRowClick?: (id: string) => void;
}

const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 45%, 55%)`;
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const getPrimaryRole = (roles: string[], status?: string): string => {
  if (status === 'Offboarding') {
    return 'Offboarding';
  }
  if (status === 'Inactive') {
    return 'Offboarded';
  }
  if (roles.length > 0) {
    return roles[0];
  }
  return 'No role';
};

export const UserListItem = React.memo(function UserListItem({
  id,
  fullName,
  email,
  roles,
  status,
  loading,
  onOpenMenu,
  onRowClick,
}: UserListItemProps) {
  const handleClick = useCallback(() => {
    onRowClick?.(id);
  }, [onRowClick, id]);

  const handleMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onOpenMenu(e, id);
    },
    [onOpenMenu, id]
  );

  const primaryRole = getPrimaryRole(roles, status);
  const isOffboarding = primaryRole === 'Offboarding';
  const isOffboarded = primaryRole === 'Offboarded';

  return (
    <ListItemButton
      onClick={handleClick}
      data-testid="user-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Avatar
        sx={{
          width: 32,
          height: 32,
          fontSize: '0.75rem',
          fontWeight: 600,
          bgcolor: stringToColor(fullName),
          flexShrink: 0,
        }}
      >
        {getInitials(fullName)}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            fontWeight={600}
            data-testid="user-name"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {fullName}
          </Typography>
          <Chip
            label={primaryRole}
            size="small"
            data-testid="user-primary-role"
            sx={{
              fontSize: '0.7rem',
              height: 20,
              bgcolor:
                isOffboarded || isOffboarding ? 'error.main' : 'action.hover',
              color:
                isOffboarded || isOffboarding
                  ? 'error.contrastText'
                  : 'text.secondary',
              border: 'none',
              flexShrink: 0,
            }}
          />
        </Box>
        <Typography
          variant="caption"
          color="text.secondary"
          data-testid="user-email"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {email}
        </Typography>
      </Box>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={loading}
        data-testid="user-actions"
        sx={{ flexShrink: 0 }}
      >
        <MoreHorizontalIcon size={18} />
      </IconButton>
    </ListItemButton>
  );
});
